/**
 * <seed-localization> — language + country/currency panel.
 *
 * Selecting an option is a plain form submit (each button carries its own
 * name/value), so switching works without this script. What lives here is
 * opening, anchoring the panel under whichever trigger was used, filtering the
 * country list, and focus handling.
 */
class SeedLocalization extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-seed-loc-panel]');
    this.list = this.querySelector('[data-seed-loc-list]');
    this.filter = this.querySelector('[data-seed-loc-filter]');
    this.empty = this.querySelector('[data-seed-loc-empty]');
    this.activeTrigger = null;

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.reposition = this.reposition.bind(this);

    this.querySelector('[data-seed-loc-backdrop]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-seed-loc-close]')?.addEventListener('click', () => this.close());
    this.filter?.addEventListener('input', () => this.applyFilter());

    document.querySelectorAll('[data-seed-loc-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (this.isOpen && this.activeTrigger === trigger) {
          this.close();
        } else {
          this.open(trigger);
        }
      });
    });
  }

  get isOpen() {
    return !this.hidden;
  }

  open(trigger) {
    this.activeTrigger = trigger || null;
    this.hidden = false;
    this.reposition();
    // Next frame, so the transition has a "closed" state to start from.
    requestAnimationFrame(() => this.classList.add('is-open'));

    document.querySelectorAll('[data-seed-loc-trigger]').forEach((el) => {
      el.setAttribute('aria-expanded', String(el === trigger));
    });

    document.addEventListener('keydown', this.onDocumentKeydown);
    window.addEventListener('resize', this.reposition);
    window.addEventListener('scroll', this.reposition, { passive: true });

    const focusTarget =
      this.filter || this.querySelector('.seed-loc__chip.is-current') || this.querySelector('button');
    focusTarget?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');

    document.querySelectorAll('[data-seed-loc-trigger]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });

    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.reposition);
    window.removeEventListener('scroll', this.reposition);

    const trigger = this.activeTrigger;
    const finish = () => {
      this.hidden = true;
      trigger?.focus({ preventScroll: true });
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      // Match the panel transition rather than guessing a shorter delay.
      this.panel?.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  }

  /** Anchor the panel under the trigger that opened it (desktop only). */
  reposition() {
    if (!this.panel || !this.activeTrigger) return;
    if (window.matchMedia('(max-width: 749px)').matches) return;

    const rect = this.activeTrigger.getBoundingClientRect();
    const width = this.panel.offsetWidth;
    const margin = 16;
    // Right-align to the trigger, then pull back inside the viewport if needed.
    let right = window.innerWidth - rect.right;
    right = Math.min(Math.max(right, margin), Math.max(margin, window.innerWidth - width - margin));

    this.panel.style.top = `${rect.bottom + 10}px`;
    this.panel.style.right = `${right}px`;
    this.panel.style.left = 'auto';
  }

  applyFilter() {
    if (!this.list) return;
    const term = this.filter.value.trim().toLowerCase();
    let matches = 0;

    this.list.querySelectorAll('[data-seed-loc-country]').forEach((item) => {
      const hit = !term || item.dataset.seedLocCountry.includes(term);
      item.hidden = !hit;
      if (hit) matches += 1;
    });

    if (this.empty) this.empty.hidden = matches !== 0;
  }
}

if (!customElements.get('seed-localization')) {
  customElements.define('seed-localization', SeedLocalization);
}
